import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MapService } from '../../../../../shared/services/map.service';
import { DateFormatPipe } from '../../../../../shared/pipes';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment/moment';
import { projectsList } from '../../../../projects/core/projects.selectors';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';

@Component({
    selector: 'app-project-location-map',
    templateUrl: './project-location-map.component.html',
    styleUrls: ['./project-location-map.component.scss'],
    standalone: false
})
export class ProjectLocationMapComponent implements OnInit, OnDestroy {
  isFullScreen = false;
  mapComponentInstance: any;
  markersList: any[] = [];
  projects: any[] = [];
  showTooltip = false;
  tooltipContent = '';
  screenPoint = {
    x: 0,
    y: 0,
  };
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private mapService: MapService,
    private dateFormatPipe: DateFormatPipe,
    private store: Store<fromRoot.State>,
  ) {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.isFullScreen = 'fs' in qp;
      });
  }

  ngOnInit() {
    this.store.select(projectsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data.length > 0) {
          const tempData = JSON.parse(JSON.stringify(data));
          this.createMarkersList(tempData);
          this.drawPointsOnMap();
        }
      });
  }

  createMarkersList(projects) {
    // while creating marker, add level = 2
    // this is needed for the esri map component to show tooltip
    this.markersList = projects.map(project => {
      const location = {
        longitude: project.longitude,
        latitude: project.latitude,
      };

      const data = {
        ...project,
        level: 2,
      };

      return this.mapService.createMarker(location, data, 'project');
    });

  }

  onMapComponentLoaded($event: any) {
    this.mapComponentInstance = $event;
    this.drawPointsOnMap();
  }

  drawPointsOnMap() {
    if (this.mapComponentInstance && this.markersList.length) {
      // Clear all markers previously drawn on the map
      this.mapService.removeAllMarkers(this.mapComponentInstance);

      // Draw markers on the map
      this.mapComponentInstance?.drawProjectSiteMarkers(this.markersList);

      // Reset the zoom level to get all points on available screen
      this.mapService.setExtent(this.mapComponentInstance, this.markersList, 2.2);
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  protected _onShowTooltip($event) {
    if ($event) {
      this.showTooltip = true;
      const category = $event?.graphic?.attributes?.category;

      const data = {
        ...$event?.graphic?.attributes?.data,
        markerCategory: category,
      };

      this.screenPoint = $event?.screenPoint;

      const project = $event?.graphic?.attributes?.data;

      const lastUpdated = moment.max(moment(project.lastUpdatedDailyReportDate, 'YYYY-MM-DD'),
        moment(project.lastUpdatedIssueDate, 'YYYY-MM-DD'));

      let updatedAtLabel = '';

      if (moment(lastUpdated).isValid()) {
        updatedAtLabel =
          ` <span class="text-type-15 tooltip-light" style="font-weight: 400;">(${this.dateFormatPipe.transform(
            lastUpdated, 'ago')} ago)</span>`;
      }

      this.tooltipContent =
        `<span class="text-type-15 tooltip-bold" style="color: #060b1e;">${project.name}</span>${updatedAtLabel}`;
    } else {
      this.showTooltip = false;
    }
  }
}
